import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getDrug, getDrugComparison, generateAIBrief, generateCommitteePackPdf } from '../services/api';
import EvidenceDrawer from '../components/EvidenceDrawer';
import { toast } from 'sonner';
import { 
  ChevronRight, BookOpen, Download, Send, Shield, AlertTriangle, 
  Sparkles, Globe, TrendingUp, FlaskConical, ExternalLink, MapPin,
  Database, Clock, CheckCircle2, FileText, Info, Lock, Unlock
} from 'lucide-react';

// ── Confidence Badge ───────────────────────────────────────

function ConfidenceBadge({ level, size = "sm" }) {
  const config = {
    high:    { bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-700", label: "High Confidence", dot: "bg-emerald-500" },
    moderate:{ bg: "bg-amber-50 border-amber-200",    text: "text-amber-700",   label: "Moderate",        dot: "bg-amber-500" },
    low:     { bg: "bg-red-50 border-red-200",         text: "text-red-700",     label: "Low Confidence",  dot: "bg-red-500" },
    unknown: { bg: "bg-slate-100 border-slate-200",    text: "text-slate-500",   label: "Unknown",         dot: "bg-slate-400" },
  };
  const c = config[level] || config.unknown;
  const px = size === "lg" ? "px-3 py-1 text-xs" : "px-2 py-0.5 text-[10px]";
  return (
    <span className={`inline-flex items-center gap-1.5 font-semibold rounded-full border ${c.bg} ${c.text} ${px}`} data-testid={`confidence-${level}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  );
}

// ── Source Attribution Tag ──────────────────────────────────

function SourceTag({ name, url }) {
  if (url) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors" data-testid="source-tag">
        <Database className="w-2.5 h-2.5" /> {name} <ExternalLink className="w-2 h-2" />
      </a>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 border border-slate-200" data-testid="source-tag">
      <Database className="w-2.5 h-2.5" /> {name}
    </span>
  );
}

// ── Simulated Data Banner ──────────────────────────────────

function SimulatedBanner({ label }) {
  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 flex items-start gap-3 mb-6" data-testid="simulated-banner">
      <Info className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-xs font-semibold text-indigo-700">Demo Mode — Simulated Dataset</p>
        <p className="text-[10px] text-indigo-600 mt-0.5">{label}</p>
      </div>
    </div>
  );
}

// ── Regulatory Entry Row ───────────────────────────────────

const REGION_FLAGS = { US: '🇺🇸', EU: '🇪🇺', UK: '🇬🇧', IN: '🇮🇳', ZA: '🇿🇦', AE: '🇦🇪', JP: '🇯🇵', AU: '🇦🇺', BR: '🇧🇷' };

function RegEntry({ entry }) {
  const isApproved = entry.status === "approved" || entry.status === "interchangeable";
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-2.5">
        <span className="text-base">{REGION_FLAGS[entry.region] || '🌐'}</span>
        <div>
          <span className="text-sm font-medium text-slate-800">{entry.region}</span>
          {entry.application_number && <span className="ml-2 text-[10px] text-slate-400 font-mono">{entry.application_number}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <SourceTag name={entry.source_name} />
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isApproved ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'}`}>
          {isApproved ? <CheckCircle2 className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" /> : null}
          {entry.status?.replace('_', ' ').toUpperCase()}
        </span>
      </div>
    </div>
  );
}

// ── Patent Row ─────────────────────────────────────────────

function PatentRow({ patent }) {
  const isExpired = patent.status === "expired" || (patent.expiry_date && patent.expiry_date < new Date().toISOString().slice(0, 10));
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-2.5">
        {isExpired ? <Unlock className="w-3.5 h-3.5 text-emerald-500" /> : <Lock className="w-3.5 h-3.5 text-amber-500" />}
        <div>
          <span className="text-sm font-mono font-medium text-slate-700">{patent.patent_number}</span>
          <span className="ml-2 text-[10px] text-slate-400">{patent.type}</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <SourceTag name={patent.source_name} />
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isExpired ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
          {isExpired ? 'EXPIRED' : `Expires ${patent.expiry_date}`}
        </span>
      </div>
    </div>
  );
}

// ── Generic Card (Source-Backed) ───────────────────────────

function GenericCard({ generic, rank, onViewEvidence }) {
  const cs = generic.confidence_summary || {};
  const badge = {
    recommended: 'text-emerald-700 bg-emerald-50 border border-emerald-200',
    pharmacist_review: 'text-amber-700 bg-amber-50 border border-amber-200',
    clinical_review: 'text-red-700 bg-red-50 border border-red-200',
  }[generic.review_status] || 'text-slate-600 bg-slate-100 border border-slate-200';
  const badgeLabel = { recommended: 'Recommended', pharmacist_review: 'Pharmacist Review', clinical_review: 'Clinical Review' }[generic.review_status] || generic.review_status;

  const confColor = generic.confidence >= 85 ? 'text-emerald-600' : generic.confidence >= 70 ? 'text-amber-600' : 'text-red-600';
  const scores = generic.scores || {};
  const be = generic.bioequivalence || {};

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-lg hover:border-slate-300 transition-all" data-testid={`generic-card-${generic.id}`}>
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${rank === 1 ? 'bg-[#0A2540] text-white' : 'bg-slate-100 text-slate-500'}`}>{rank}</div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-slate-900 truncate">{generic.name}</h3>
          <p className="text-xs text-slate-400">{generic.manufacturer}</p>
        </div>
        <ConfidenceBadge level={cs.level || "unknown"} />
      </div>

      {/* Key Metrics */}
      <div className="flex items-center gap-4 mb-3">
        <div className="relative w-14 h-14 flex-shrink-0">
          <svg viewBox="0 0 36 36" className="w-14 h-14 -rotate-90">
            <circle cx="18" cy="18" r="14" fill="none" stroke="#f1f5f9" strokeWidth="3" />
            <circle cx="18" cy="18" r="14" fill="none" stroke={generic.confidence >= 85 ? '#10b981' : generic.confidence >= 70 ? '#f59e0b' : '#ef4444'} strokeWidth="3" strokeDasharray={`${generic.confidence * 0.88} 100`} strokeLinecap="round" />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-xs font-black ${confColor}`}>{generic.confidence}%</span>
          </div>
        </div>
        <div className="flex-1 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-slate-400 uppercase tracking-wider">Savings</span>
            <span className="text-sm font-bold text-emerald-600">{Math.round((generic.estimated_savings_percent || 0) * 100)}%</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-slate-400 uppercase tracking-wider">BE Rating</span>
            <span className="text-[10px] font-semibold text-slate-700">{be.rating || 'N/A'}</span>
          </div>
          <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full ${badge}`}>{badgeLabel}</span>
        </div>
      </div>

      {/* Compact score bars */}
      <div className="space-y-1 mb-3">
        {[{ label: 'EFF', score: scores.efficacy || 0 }, { label: 'REG', score: scores.regulatory || 0 }, { label: 'AVL', score: scores.availability || 0 }].map(({ label, score }) => (
          <div key={label} className="flex items-center gap-2">
            <span className="text-[9px] font-semibold text-slate-400 w-6 text-right">{label}</span>
            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${score >= 85 ? 'bg-emerald-500' : score >= 70 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${score}%` }} />
            </div>
            <span className="text-[10px] font-semibold text-slate-500 w-5">{score}</span>
          </div>
        ))}
      </div>

      {/* Source attribution */}
      <div className="flex flex-wrap gap-1 mb-3">
        {(generic.data_sources || []).slice(0, 3).map(src => (
          <span key={src} className="text-[8px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-100">{src.replace('src-', '').toUpperCase()}</span>
        ))}
        {generic.risk_flags?.filter(Boolean).length > 0 && (
          <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200">
            <AlertTriangle className="w-2 h-2 inline -mt-0.5 mr-0.5" />{generic.risk_flags.filter(Boolean).length} flag{generic.risk_flags.filter(Boolean).length > 1 ? 's' : ''}
          </span>
        )}
      </div>

      <button onClick={() => onViewEvidence(generic)} className="w-full py-2 text-xs font-semibold text-[#0A2540] bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors flex items-center justify-center gap-1.5" data-testid={`view-evidence-${generic.id}`}>
        <BookOpen className="w-3.5 h-3.5" /> View Evidence
      </button>
    </div>
  );
}

// ── Insufficient Data State ────────────────────────────────

function InsufficientDataCard({ message }) {
  return (
    <div className="col-span-3 bg-slate-50 border-2 border-dashed border-slate-300 rounded-2xl p-8 text-center" data-testid="insufficient-data">
      <Shield className="w-10 h-10 text-slate-300 mx-auto mb-3" />
      <p className="text-sm font-semibold text-slate-600">{message || "Insufficient verified data"}</p>
      <p className="text-xs text-slate-400 mt-1">This system does not fabricate or infer regulatory data. Contact data team for source acquisition.</p>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────

export default function MoleculeDetailPage() {
  const { drugId } = useParams();
  const [drug, setDrug] = useState(null);
  const [generics, setGenerics] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [dataSourcesDetail, setDataSourcesDetail] = useState([]);
  const [confidenceSummary, setConfidenceSummary] = useState({});
  const [simulatedData, setSimulatedData] = useState(false);
  const [simulatedLabel, setSimulatedLabel] = useState('');
  const [insufficientData, setInsufficientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiBrief, setAiBrief] = useState(null);
  const [briefMode, setBriefMode] = useState('pharmacy');
  const [loadingBrief, setLoadingBrief] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedGeneric, setSelectedGeneric] = useState(null);
  const [generatingPack, setGeneratingPack] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const [drugRes, comparisonRes] = await Promise.all([getDrug(drugId), getDrugComparison(drugId)]);
        setDrug(drugRes.data.drug);
        setGenerics(comparisonRes.data.generics || []);
        setEvidence(drugRes.data.evidence || []);
        setDataSourcesDetail(drugRes.data.data_sources_detail || []);
        setConfidenceSummary(drugRes.data.confidence_summary || {});
        setSimulatedData(drugRes.data.simulated_data || false);
        setSimulatedLabel(drugRes.data.simulated_label || '');
        if (drugRes.data.insufficient_data) {
          setInsufficientData(drugRes.data.message);
        }
      } catch (error) {
        console.error('Failed to fetch drug data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [drugId]);

  const handleGenerateBrief = async (mode) => {
    setBriefMode(mode);
    setLoadingBrief(true);
    try {
      const res = await generateAIBrief(drugId, mode);
      setAiBrief(res.data.brief);
    } catch (error) {
      console.error('Failed to generate brief:', error);
    } finally {
      setLoadingBrief(false);
    }
  };

  const handleViewEvidence = (generic) => { setSelectedGeneric(generic); setDrawerOpen(true); };

  const handleGenerateCommitteePack = async () => {
    if (generics.length === 0) { toast.error('No generic alternatives available'); return; }
    setGeneratingPack(true);
    try {
      const genericIds = generics.map(g => g.id);
      const res = await generateCommitteePackPdf(drugId, genericIds);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Committee_Pack_${drug?.brand_name || 'Drug'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Committee Pack PDF downloaded');
    } catch (error) {
      toast.error('Failed to generate Committee Pack');
    } finally {
      setGeneratingPack(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6" data-testid="molecule-loading">
        <div className="skeleton h-8 w-48 mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4"><div className="skeleton h-48 rounded-xl" /><div className="skeleton h-64 rounded-xl" /></div>
          <div className="skeleton h-96 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!drug) {
    return (
      <div className="p-6 text-center">
        <p className="text-slate-500">Drug not found</p>
        <Link to="/search" className="text-[#0A2540] hover:underline mt-2 inline-block">Return to search</Link>
      </div>
    );
  }

  const topGenerics = generics.slice(0, 3);
  const regEntries = drug.regulatory_entries || [];
  const patentRecords = drug.patent_records || [];

  return (
    <div className="p-6" data-testid="molecule-detail-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
        <Link to="/dashboard" className="hover:text-slate-700">Dashboard</Link>
        <ChevronRight className="w-4 h-4" />
        <Link to="/search" className="hover:text-slate-700">Search</Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-slate-900">{drug.brand_name}</span>
      </div>

      {/* Simulated Data Banner */}
      {simulatedData && <SimulatedBanner label={simulatedLabel} />}

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">{drug.brand_name}</h1>
            <ConfidenceBadge level={confidenceSummary.level || "unknown"} size="lg" />
          </div>
          <p className="text-base text-slate-600">{drug.generic_name}</p>
          <p className="text-sm text-slate-400 mt-0.5">{drug.indication}</p>
          {/* Source count */}
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[10px] text-slate-400 flex items-center gap-1"><Database className="w-3 h-3" /> {confidenceSummary.source_count || 0} verified sources</span>
            {confidenceSummary.last_updated && <span className="text-[10px] text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Updated {confidenceSummary.last_updated}</span>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2" data-testid="generate-committee-pack-btn" onClick={handleGenerateCommitteePack} disabled={generatingPack}>
            <Download className="w-4 h-4" />{generatingPack ? 'Generating...' : 'Committee Pack PDF'}
          </button>
          <button className="bg-[#0A2540] text-white hover:bg-[#0d2d4a] px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2" data-testid="send-review-btn">
            <Send className="w-4 h-4" />Send to Review
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Summary Card */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="drug-summary-card">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              <div><p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Therapeutic Area</p><p className="text-sm font-semibold text-slate-900">{drug.therapeutic_area}</p></div>
              <div><p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Patent Status</p><p className="text-sm font-semibold text-slate-900">{drug.patent_status}</p></div>
              <div><p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Formulation</p><p className="text-sm font-semibold text-slate-900">{drug.formulation} {drug.strength}</p></div>
              <div><p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Generic Alternatives</p><p className="text-sm font-semibold text-slate-900">{generics.length}</p></div>
            </div>
          </div>

          {/* Regulatory Status by Geography */}
          {regEntries.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="regulatory-status">
              <div className="flex items-center gap-2 mb-4">
                <Globe className="w-5 h-5 text-[#0A2540]" />
                <h2 className="text-base font-bold text-slate-900">Regulatory Status by Geography</h2>
              </div>
              <div>{regEntries.map((e, i) => <RegEntry key={i} entry={e} />)}</div>
            </div>
          )}

          {/* Patent & Exclusivity */}
          {patentRecords.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="patent-status">
              <div className="flex items-center gap-2 mb-4">
                <Lock className="w-5 h-5 text-[#0A2540]" />
                <h2 className="text-base font-bold text-slate-900">Patent & Exclusivity Records</h2>
              </div>
              <div>{patentRecords.map((p, i) => <PatentRow key={i} patent={p} />)}</div>
            </div>
          )}

          {/* Top Alternatives */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-slate-900">Top Validated Alternatives</h2>
              <Link to={`/compare/${drugId}`} className="text-sm text-[#0A2540] font-medium hover:underline flex items-center gap-1">Full comparison <ChevronRight className="w-4 h-4" /></Link>
            </div>
            {insufficientData ? (
              <InsufficientDataCard message={insufficientData} />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {topGenerics.map((generic, idx) => (
                  <GenericCard key={generic.id} generic={generic} rank={idx + 1} onViewEvidence={handleViewEvidence} />
                ))}
                {topGenerics.length === 0 && (
                  <div className="col-span-3 bg-white border border-slate-200 rounded-2xl p-8 text-center">
                    <p className="text-sm text-slate-500">No generic alternatives currently available in tracked markets</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Risk Flags */}
          {generics.some(g => g.risk_flags?.filter(Boolean).length > 0) && (
            <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="risk-flags-section">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-5 h-5 text-amber-500" />
                <h2 className="text-base font-bold text-slate-900">Clinical Governance Flags</h2>
              </div>
              <div className="space-y-3">
                {generics.filter(g => g.risk_flags?.filter(Boolean).length > 0).map(generic => (
                  <div key={generic.id} className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800 text-sm">{generic.name}</p>
                      <ul className="mt-1 space-y-0.5">
                        {generic.risk_flags.filter(Boolean).map((flag, idx) => (
                          <li key={idx} className="text-xs text-amber-700">{flag}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Evidence Snapshots */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="evidence-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-slate-400" />
                <h2 className="text-base font-bold text-slate-900">Evidence Snapshots</h2>
              </div>
            </div>
            <div className="space-y-3">
              {evidence.slice(0, 5).map(ev => (
                <div key={ev.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-slate-900 text-sm">{ev.title}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-1.5">
                        <span className="text-xs text-slate-500">{ev.study_type}</span>
                        <span className="text-slate-300">|</span>
                        <span className="text-xs text-slate-500">{ev.year}</span>
                        <span className="text-slate-300">|</span>
                        <ConfidenceBadge level={ev.confidence_level === 'High' ? 'high' : ev.confidence_level === 'Moderate' ? 'moderate' : 'low'} />
                        {ev.source_name && <SourceTag name={ev.source_name} />}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-600 mt-2 leading-relaxed">{ev.key_takeaway}</p>
                  {ev.citation && <p className="text-[10px] text-slate-400 mt-1.5 font-mono">{ev.citation}</p>}
                </div>
              ))}
              {evidence.length === 0 && <p className="text-sm text-slate-500 text-center py-4">No evidence entries available</p>}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Data Sources */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="data-sources-panel">
            <div className="flex items-center gap-2 mb-4">
              <Database className="w-5 h-5 text-[#0A2540]" />
              <h2 className="text-sm font-bold text-slate-900">Data Sources Used</h2>
            </div>
            <div className="space-y-2.5">
              {dataSourcesDetail.map(src => (
                <div key={src.id} className="flex items-start gap-2.5 py-2 border-b border-slate-100 last:border-0">
                  <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${src.reliability === 'high' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold text-slate-800">{src.name}</span>
                      {src.url && <a href={src.url} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-2.5 h-2.5 text-slate-400 hover:text-blue-500" /></a>}
                    </div>
                    <p className="text-[10px] text-slate-400">{src.authority} ({src.region})</p>
                  </div>
                </div>
              ))}
              {dataSourcesDetail.length === 0 && <p className="text-xs text-slate-400">No source data available</p>}
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-[10px] text-slate-400">{confidenceSummary.methodology}</p>
            </div>
          </div>

          {/* AI Insight Panel */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="ai-insight-panel">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-5 h-5 text-[#0A2540]" />
              <h2 className="text-sm font-bold text-slate-900">AI Insight Panel</h2>
            </div>
            <p className="text-[10px] text-slate-400 mb-3">AI summarizes structured data only. It does not determine generic availability, patent status, or regulatory claims.</p>
            <div className="flex gap-2 mb-4">
              {['cfo', 'pharmacy'].map(mode => (
                <button key={mode} onClick={() => handleGenerateBrief(mode)}
                  className={`flex-1 py-2 px-3 text-xs font-medium rounded-lg transition-colors ${briefMode === mode && aiBrief ? 'bg-[#0A2540] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  data-testid={`${mode}-mode-btn`}>{mode === 'cfo' ? 'CFO Mode' : 'Pharmacy Mode'}</button>
              ))}
            </div>
            {loadingBrief ? (
              <div className="space-y-3"><div className="skeleton h-4 w-full" /><div className="skeleton h-4 w-3/4" /><div className="skeleton h-4 w-5/6" /></div>
            ) : aiBrief ? (
              <div className="whitespace-pre-wrap text-xs text-slate-600 leading-relaxed">{aiBrief}</div>
            ) : (
              <div className="text-center py-4 text-xs text-slate-400">Select a mode to generate insights</div>
            )}
          </div>

          {/* Savings Estimate */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5" data-testid="savings-estimate">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              <h2 className="text-sm font-bold text-slate-900">Savings Estimate</h2>
            </div>
            <div className="text-center py-3">
              <p className="text-3xl font-bold text-emerald-600">
                {generics.length > 0 ? `${Math.round((generics[0]?.estimated_savings_percent || 0.15) * 100)}%` : 'N/A'}
              </p>
              <p className="text-xs text-slate-600 mt-1">Potential cost reduction</p>
              <p className="text-[10px] text-slate-400 mt-0.5">Based on deterministic analysis, not AI-generated</p>
            </div>
            <Link to="/simulator" className="mt-3 bg-emerald-600 text-white hover:bg-emerald-700 w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-medium transition-colors">
              <FlaskConical className="w-3.5 h-3.5" /> Run Simulation
            </Link>
          </div>
        </div>
      </div>

      <EvidenceDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} generic={selectedGeneric} evidence={evidence} drugId={drugId} />
    </div>
  );
}
